import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { assetCsvFields } from '../components/csv/assetCsvFields';
import { saveAs } from 'file-saver';
import { CircularProgress, Box, Typography } from '@mui/material';
import { useAssetApi } from '../api/useAssetApi';

const CsvDownloadPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { getAssetsApi } = useAssetApi();
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState<number | null>(null);
  const [totalPages, setTotalPages] = useState<number | null>(null);


  const parseQuery = () => {
    const params = new URLSearchParams(location.search);
    return {
      client: params.get('client'),
      start_date: params.get('start_date'),
      end_date: params.get('end_date'),
      fields: params.get('fields')?.split(',') || [],
    };
  };

  useEffect(() => {
    const fetchAndDownload = async () => {
      const { client, start_date, end_date, fields } = parseQuery();

      if (!client || !start_date || !end_date) {
        alert("Missing required query params.");
        setLoading(false);
        return navigate('/');
      }

      try {
        const perPage = 100;
        let page = 1;
        let totalAssets = 0;
        let allAssets: any[] = [];

        setCurrentPage(1); // first page being fetched

        // First call
        const firstResponse = await getAssetsApi({
          page,
          per_page: perPage,
          system_code: client,
          start_date,
          end_date,
        });

        allAssets = firstResponse.assets;
        totalAssets = firstResponse.total;

        const total = Math.ceil(totalAssets / perPage);
        setTotalPages(total);

        // Fetch remaining
        for (page = 2; page <= total; page++) {
          setCurrentPage(page);

          const response = await getAssetsApi({
            page,
            per_page: perPage,
            system_code: client,
            start_date,
            end_date,
          });

          allAssets.push(...response.assets);
        }

        const selectedFields = fields.length > 0
          ? assetCsvFields.filter(f => fields.includes(f.key))
          : assetCsvFields;

        const csvHeaders = selectedFields.map(f => f.label);
        const csvRows = allAssets.map(v =>
          selectedFields.map(f => f.extractor ? f.extractor(v) : '')
        );

        const csvContent = [csvHeaders, ...csvRows].map(row => row.join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

        saveAs(blob, `assets_export_${Date.now()}.csv`);
      } catch (err) {
        console.error("CSV download error:", err);
        alert("Error generating CSV.");
      } finally {
        setLoading(false);
        setCurrentPage(null);
        setTotalPages(null);
        navigate(-1);
      }
    };


    fetchAndDownload();
  }, []);

  if (!loading) return null;

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
    >
      <CircularProgress />
      <Typography variant="body1" mt={2}>
        Generating your CSV...
      </Typography>

      {currentPage && totalPages && (
        <Typography variant="body2" mt={1}>
          Downloading page {currentPage} of {totalPages}
        </Typography>
      )}
    </Box>
  );

};

export default CsvDownloadPage;
